import { ehEvento, homeSemCarrossel } from "@/constants/types/tipoTotem";

// Cache sem TTL — invalidado apenas quando o gestão altera as fotos
// (via listenerCleanCache que limpa o localStorage antes do reload)
// As imagens são convertidas para DataURL e salvas no localStorage,
// garantindo zero chamadas ao Firebase Storage após o primeiro carregamento.
// Versão na chave: ao evoluir o schema do cache (ex.: novo campo botaoCor),
// basta incrementar o sufixo para invalidar caches antigos automaticamente.
// Totens do tipo EVENTO têm Home própria (personalizaTotem/{uid}) e por isso
// precisam de uma entrada de cache separada da global — daí o sufixo com o UID.
// HOME_CONFIG_CACHE_BASE é a base comum a TODAS as chaves (todas as versões e
// fontes) e é o que a limpeza de cache do comando de manutenção varre.
export const HOME_CONFIG_CACHE_BASE = "homeConfig";
const HOME_CONFIG_CACHE_PREFIX = `${HOME_CONFIG_CACHE_BASE}_v4`;

// `semGaleria` entra na chave: a Home de evento é gravada em cache sem as
// fotos da galeria, então ela não pode reaproveitar (nem sobrescrever) a
// entrada de um totem que usa carrossel.
export function homeConfigCacheKey(totemId, semGaleria) {
  const base = totemId
    ? `${HOME_CONFIG_CACHE_PREFIX}_${totemId}`
    : HOME_CONFIG_CACHE_PREFIX;
  return semGaleria ? `${base}_fixo` : base;
}

async function urlToDataUrl(url) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
}

async function enrichWithDataUrls(data, comGaleria = true) {
  const topo = data.topo
    ? { ...data.topo, dataUrl: await urlToDataUrl(data.topo.url) }
    : null;

  // Sem galeria (totem de evento) nem baixamos as fotos: seriam 7+ imagens
  // convertidas para base64 e guardadas no localStorage para nunca aparecer.
  const galeria = comGaleria
    ? await Promise.all(
        (data.galeria || []).map(async (foto) => ({
          ...foto,
          dataUrl: await urlToDataUrl(foto.url),
        }))
      )
    : [];

  // Falha parcial: o Firestore respondeu, mas alguma imagem (que TEM url) não
  // baixou/converteu (dataUrl nulo). No modo estrito (refresh do gestão) isso é
  // um erro — não aplicamos uma config incompleta por cima de dados bons; o
  // chamador mantém o que já estava carregado. No primeiro acesso (não estrito)
  // seguimos tolerantes: mostra o que der.
  // if (strict) {
  //   const incompleto =
  //     (topo && topo.url && !topo.dataUrl) ||
  //     galeria.some((foto) => foto.url && !foto.dataUrl);
  //   if (incompleto) {
  //     throw new Error("home-config-imagens-incompletas");
  //   }
  // }

  return { topo, galeria, botaoCor: data.botaoCor || null };
}

// De onde este totem carrega a Home. EVENTO → doc próprio; qualquer outro
// valor (inclusive ausente) → config global.
function fonteConfig(rootGetters) {
  const totem = rootGetters["totem/getTotem"] || {};
  return ehEvento(totem.tipoTotem) && totem.id ? totem.id : null;
}

// Totem de evento: a Home é uma foto única, a galeria não é exibida em lugar
// nenhum. O tipo lembrado cobre o boot, antes do doc do totem chegar.
function ehLayoutFixo(rootGetters) {
  return homeSemCarrossel(rootGetters["totem/getTotem"]);
}

export default {
  namespaced: true,
  state: {
    topo: null,
    galeria: [],
    botaoCor: null,
    loaded: false,
  },
  mutations: {
    SET_HOME_CONFIG(state, { topo, galeria, botaoCor }) {
      state.topo = topo || null;
      state.galeria = Array.isArray(galeria) ? galeria : [];
      state.botaoCor = botaoCor || null;
      state.loaded = true;
    },
  },
  getters: {
    getTopo: (state) => state.topo,
    getGaleria: (state) =>
      [...state.galeria].sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    getBotaoCor: (state) => state.botaoCor,
    isLoaded: (state) => state.loaded,
    // UID quando a Home vem de personalizaTotem/{uid}; null quando vem da global
    getFonteTotemId: (state, getters, rootState, rootGetters) =>
      fonteConfig(rootGetters),
  },
  actions: {
    // payload.force = true → refresh disparado pelo gestão: ignora Vuex/cache e
    // rebusca do Firebase. IMPORTANTE: só sobrescreve estado/cache se a busca der
    // certo. Se falhar (ex.: internet caiu), os dados já carregados são mantidos
    // — nada é limpo e nenhum reload ocorre.
    async fetchHomeConfig({ commit, state, rootGetters }, payload) {
      const force = !!(payload && payload.force);
      const totemId = fonteConfig(rootGetters);
      const layoutFixo = ehLayoutFixo(rootGetters);
      const cacheKey = homeConfigCacheKey(totemId, layoutFixo);

      // Vuex já carregado (mesma sessão) — sem custo algum
      if (!force && state.loaded) return;

      // LocalStorage com DataURLs persistidos — sem custo Firebase algum
      if (!force) {
        try {
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            commit("SET_HOME_CONFIG", JSON.parse(cached));
            return;
          }
        } catch (_) {
          // ignora erros de JSON.parse ou storage
        }
      }

      // Primeiro acesso ou refresh forçado pelo gestão:
      // 1. Lê URLs do Firestore
      // 2. Baixa cada imagem e converte para DataURL
      // 3. Persiste tudo no localStorage
      // Se qualquer passo lançar, o erro sobe para o chamador SEM ter alterado
      // o estado/cache atuais (o commit só acontece no fim, em caso de sucesso).
      const { loadHomeConfig } = await import("@/service/firebase");
      const raw = await loadHomeConfig(totemId);
      const data = await enrichWithDataUrls(raw, !layoutFixo);

      try {
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch (_) {
        // Quota excedida (imagens muito grandes) — mantém só em Vuex
      }

      commit("SET_HOME_CONFIG", data);
    },

    // Atualização AO VIVO: recebe o doc bruto do onSnapshot(config/home),
    // reenriquece as imagens (base64) e atualiza Vuex + cache. Trava: se as
    // imagens vierem incompletas (ex.: snapshot chegou offline) e já houver algo
    // carregado, ignora — não substitui dados bons em tela por uma versão sem
    // imagens.
    async applyHomeConfig({ commit, state, rootGetters }, raw) {
      const layoutFixo = ehLayoutFixo(rootGetters);
      const data = await enrichWithDataUrls(raw || {}, !layoutFixo);
      const cacheKey = homeConfigCacheKey(fonteConfig(rootGetters), layoutFixo);

      const imagensIncompletas =
        (data.topo && data.topo.url && !data.topo.dataUrl) ||
        data.galeria.some((foto) => foto.url && !foto.dataUrl);
      if (imagensIncompletas && state.loaded) return;

      try {
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch (_) {
        // Quota excedida — mantém só em Vuex
      }

      commit("SET_HOME_CONFIG", data);
    },
  },
};
